import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { useGetDefaultRibbonFilters } from 'src/pages/controls/useGetDefaultRibbonFilters';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetControlsRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';

import { useGetCollectionTableProps } from './config';
import { ControlRegisterToolbar } from './ControlRegisterToolbar';
import TestResultModal from './update/tabs/TestResultModal';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'controls' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [openTestResult, setOpenTestResult] = useState<
    { controlId: string; ratingId: string } | undefined
  >();

  const { data, loading, refetch } = useGetControlsRegister({
    queryArgs: {},
  });
  const handleTestResultModalClose = () => {
    setOpenTestResult(undefined);
    setIsEditOpen(false);
    refetch();
  };

  const tableProps = useGetCollectionTableProps((testResult) => {
    setOpenTestResult(testResult);
    setIsEditOpen(true);
  }, data?.control);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  // Shared by the legacy PageLayout counter and the object toolbar: the toolbar
  // renders `header.counter` verbatim and does NOT add parentheses (see
  // `ObjectToolbarHeader`), so both paths take the already-formatted `(12)`.
  // `getCounter` returns '' while loading, which the toolbar treats as no count.
  const counter = getCounter(tableProps.totalItemsCount, loading);
  const title = st('register_title');

  return (
    <PageLayout
      helpTranslationKey={'controls.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-153) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <ControlRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.Control}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
      {isEditOpen && openTestResult && (
        <TestResultModal
          parentControlId={openTestResult.controlId}
          Id={openTestResult.ratingId}
          onDismiss={handleTestResultModalClose}
          assessmentMode={'rating'}
        />
      )}
    </PageLayout>
  );
};

export default Page;
